import React, { useMemo, useCallback } from 'react';
import { StyleSheet, View, Pressable } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { useModalStore } from '@/store';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { formatCreditBalance } from '@/lib/formatCredits';
import { formatCreditResetSchedule } from '@/lib/creditResetDate';

const createStyles = (colors) => StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.solid_color_tertiary,
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 14,
    gap: 8,
  },
  iconContainer: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: colors.solid_color_secondary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  textContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flexWrap: 'wrap',
  },
  dot: {
    width: 3,
    height: 3,
    borderRadius: 1.5,
    backgroundColor: colors.text_color_tertiary,
  },
  infoButton: {
    paddingVertical: 4,
    paddingHorizontal: 6,
    borderRadius: 6,
  },
  infoButtonHovered: {
    backgroundColor: colors.solid_color_secondary,
  },
});

function CompactBalanceBar({ totalAvailableCredits, resetDate, isPlus, withInfoButton = true }) {
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const { openModal } = useModalStore(state => state.actions);

  const handleInfoPress = useCallback(() => {
    openModal('CreditsInfoModal');
  }, [openModal]);

  const formattedReset = formatCreditResetSchedule(resetDate);

  return (
    <View style={styles.container}>
      <View style={styles.iconContainer}>
        <Icon name="coins" size={14} color={colors.text_color_primary} />
      </View>
      <View style={styles.textContainer}>
        <Text size="s" weight="semibold" color="primary">
          {formatCreditBalance(totalAvailableCredits)} credits available
        </Text>
        {isPlus && formattedReset && (
          <>
            <View style={styles.dot} />
            <Text size="xs" color="tertiary">
              Resets {formattedReset}
            </Text>
          </>
        )}
      </View>
      {
        withInfoButton && (
        <Pressable
          onPress={handleInfoPress}
          style={({ hovered }) => [
            styles.infoButton,
            hovered && styles.infoButtonHovered,
          ]}
          accessibilityLabel="What are credits?"
        >
          <Icon name="circle-question" size={14} color={colors.text_color_tertiary} />
        </Pressable>
      )}
    </View>
  );
}

export default React.memo(CompactBalanceBar);
